/**
 * Client session: the access token lives only in memory (never localStorage, so XSS cannot
 * persist it). The refresh token is an HttpOnly cookie the browser sends to /auth/refresh.
 *
 * Refreshing is coordinated across tabs with the Web Locks API, and new tokens are shared over
 * a BroadcastChannel, so ten open tabs cause one refresh rather than ten (each refresh rotates
 * the cookie, and uncoordinated rotation looks like token theft to the server).
 */
import { useSyncExternalStore } from 'react'

import type { AuthOut, User, WorkspaceSummary } from '@/shared/api/types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface AuthState {
  status: AuthStatus
  user: User | null
  workspaces: WorkspaceSummary[]
  workspaceId: string | null
  accessToken: string | null
  expiresAt: number
}

const WS_KEY = 'provyn.workspace'
const CSRF = { 'X-Provyn-CSRF': '1' }
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('provyn-auth') : null

let state: AuthState = {
  status: 'loading',
  user: null,
  workspaces: [],
  workspaceId: null,
  accessToken: null,
  expiresAt: 0,
}
const listeners = new Set<() => void>()
let refreshTimer: ReturnType<typeof setTimeout> | undefined
let lastBroadcast: { body: AuthOut; at: number } | null = null

function emit() {
  listeners.forEach((l) => {
    l()
  })
}

function setState(patch: Partial<AuthState>) {
  state = { ...state, ...patch }
  emit()
}

export const authStore = {
  get: () => state,
  subscribe: (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
}

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get)
}

function storedWorkspace(): string | null {
  try {
    return localStorage.getItem(WS_KEY)
  } catch {
    return null
  }
}

function pickWorkspace(workspaces: WorkspaceSummary[], preferred: string | null): string | null {
  if (preferred && workspaces.some((w) => w.id === preferred)) return preferred
  return workspaces[0]?.id ?? null
}

export function selectWorkspace(id: string) {
  try {
    localStorage.setItem(WS_KEY, id)
  } catch {
    /* ignore */
  }
  setState({ workspaceId: id })
}

export function setWorkspaces(workspaces: WorkspaceSummary[], select?: string) {
  setState({ workspaces, workspaceId: pickWorkspace(workspaces, select ?? state.workspaceId) })
  if (select) selectWorkspace(select)
}

export function setUser(user: User) {
  setState({ user })
}

/** Apply a successful login/signup/refresh response. */
export function applyAuth(body: AuthOut, opts: { broadcast?: boolean } = {}) {
  const expiresAt = new Date(body.expires_at).getTime()
  setState({
    status: 'authenticated',
    user: body.user,
    workspaces: body.workspaces,
    workspaceId: pickWorkspace(body.workspaces, state.workspaceId ?? storedWorkspace()),
    accessToken: body.access_token,
    expiresAt,
  })
  scheduleRefresh(expiresAt)
  if (opts.broadcast !== false) channel?.postMessage({ type: 'auth', body })
}

export function clearAuth(opts: { broadcast?: boolean } = {}) {
  clearTimeout(refreshTimer)
  setState({ status: 'anonymous', user: null, workspaces: [], accessToken: null, expiresAt: 0 })
  if (opts.broadcast !== false) channel?.postMessage({ type: 'logout' })
}

function scheduleRefresh(expiresAt: number) {
  clearTimeout(refreshTimer)
  // Refresh one minute before expiry, with jitter so tabs don't all wake at once.
  const delay = Math.max(5_000, expiresAt - Date.now() - 60_000 - Math.random() * 10_000)
  refreshTimer = setTimeout(() => void refreshSession(), delay)
}

channel?.addEventListener('message', (ev: MessageEvent<{ type: string; body?: AuthOut }>) => {
  if (ev.data.type === 'auth' && ev.data.body) {
    lastBroadcast = { body: ev.data.body, at: Date.now() }
    applyAuth(ev.data.body, { broadcast: false })
  } else if (ev.data.type === 'logout') {
    clearAuth({ broadcast: false })
  }
})

let inFlight: Promise<boolean> | null = null

/** Returns true when the session is (still) valid. Safe to call concurrently. */
export function refreshSession(): Promise<boolean> {
  inFlight ??= withCrossTabLock(doRefresh).finally(() => {
    inFlight = null
  })
  return inFlight
}

async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  if ('locks' in navigator) {
    return navigator.locks.request('provyn-refresh', fn)
  }
  return fn()
}

async function doRefresh(attempt = 0): Promise<boolean> {
  // Another tab may have refreshed while we waited for the lock.
  if (lastBroadcast && Date.now() - lastBroadcast.at < 5_000) return true
  let res: Response
  try {
    res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'same-origin', headers: CSRF })
  } catch {
    return state.status === 'authenticated' // offline: keep the current session for now
  }
  if (res.ok) {
    applyAuth((await res.json()) as AuthOut)
    return true
  }
  if (res.status === 409 && attempt === 0) {
    await new Promise((r) => setTimeout(r, 400)) // a concurrent refresh won; its cookie is now set
    return doRefresh(1)
  }
  clearAuth()
  return false
}

export async function logout() {
  try {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin', headers: CSRF })
  } finally {
    clearAuth()
  }
}

/** Restore the session from the refresh cookie on page load. */
export async function bootstrapSession() {
  const ok = await refreshSession()
  if (!ok) setState({ status: 'anonymous' })
}
